import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
	getAgentInboundAnsweringPolicy,
	getAgentTransferDestinations,
	getDefaultVoiceForModel,
	type InboundAnsweringPolicy,
	isVoiceSupportedForModel,
	shortUuid,
} from "@repo/common";
import {
	ArrowUp,
	CheckCircle2,
	Copy,
	Edit,
	ExternalLink,
	FileCode2,
	Loader2,
	Phone,
	Plus,
	RotateCcw,
	Search,
	Trash2,
	Users,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import type { Agent, DeploymentStatus } from "../types";
import { isAdminRole, isSystemAdminRole } from "../utils/auth";
import { isGitHubUnavailableError } from "../utils/errorUtils";
import {
	formatPhoneNumber,
	formatPhoneNumberList,
} from "../utils/formatPhoneNumber";
import {
	normalizePhoneNumberListForStorage,
	normalizeTransferTargetListForStorage,
} from "../utils/phoneNormalization";
import { hasSuccessfulTestingDeployment } from "../utils/testingDeployment";
import { getContentFieldProps } from "../utils/textDirection";
import { AgentEditor } from "./AgentEditor";
import { PageHeader } from "./PageHeader";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardTitle } from "./ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Input } from "./ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";

type LinguiI18n = ReturnType<typeof useLingui>["i18n"];

interface AgentsPageProps {
	initialAgentName?: string | null;
	onAgentChange?: () => void;
}

type PreviewGeneratedFile = {
	path: string;
	content: string;
};

type PreviewFilesState = {
	agentId: string;
	agentName: string;
	version: number;
	generatedAt: string;
	files: PreviewGeneratedFile[];
};

type PendingDeploymentState = {
	previousDeploymentId?: string;
	previousCreatedAt?: number;
	previousStatus?: DeploymentStatus;
	previousUpdatedAt?: number;
	previousVersion?: number;
	started?: boolean;
	action?: "deploy" | "rollback";
	rollbackToVersion?: number;
};

function formatLocalDateKey(date: Date): string {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

const TERMINAL_DEPLOYMENT_STATUSES = new Set<DeploymentStatus>([
	"deploy_success",
	"deploy_error",
	"merge_error",
	"error",
]);
const START_CONFIRMED_STATUSES = new Set<DeploymentStatus>([
	"pr_opened",
	"pr_validation_started",
	"pr_merged",
	"merge_success",
	"deploy_started",
	"started",
	"deploy_success",
]);
const RUNNING_DEPLOYMENT_STATUSES = new Set<DeploymentStatus>([
	"creating_pr",
	"pr_opened",
	"pr_validation_started",
	"pr_merged",
	"merge_success",
	"deploy_started",
	"started",
]);
const FAILURE_DEPLOYMENT_STATUSES = new Set<DeploymentStatus>([
	"deploy_error",
	"merge_error",
	"error",
]);
const IN_PROGRESS_DEPLOYMENT_STATUS_LABELS: Partial<
	Record<DeploymentStatus, string>
> = {
	creating_pr: "Creating PR...",
	pr_opened: "PR Opened...",
	pr_validation_started: "Validating...",
	pr_merged: "Merging...",
	merge_success: "Building...",
	deploy_started: "Deploying...",
	started: "Deploying...",
};
const STALE_DEPLOYMENT_MS = 10 * 60_000;

type TimestampLike = {
	toMillis: () => number;
};

function toEditorTimestampMillis(value: unknown): number | null {
	if (!value) return null;
	if (
		typeof value === "object" &&
		"toMillis" in value &&
		typeof (value as TimestampLike).toMillis === "function"
	) {
		return (value as TimestampLike).toMillis();
	}
	const parsed = new Date(value as string | number | Date).getTime();
	return Number.isNaN(parsed) ? null : parsed;
}

export function normalizeAgentForEditor(agent: Agent): Agent {
	return {
		...agent,
		status: agent.status === "Paused" ? "Active" : agent.status,
		inboundAnsweringPolicy: getAgentInboundAnsweringPolicy(agent),
		realtime: {
			provider: agent.realtime?.provider || "openai",
			model: agent.realtime?.model || "gpt-realtime",
			effort: agent.realtime?.effort || "low",
		},
		gptLive: {
			enabled: agent.gptLive?.enabled === true,
			voice: agent.gptLive?.voice || "marin",
			reasoningModel: agent.gptLive?.reasoningModel || "gpt-5.6-luna",
		},
		defaultLanguage: agent.defaultLanguage || "en",
		timeZone: agent.timeZone || "Asia/Jerusalem",
		messages: agent.messages || [],
		policySections: agent.policySections || [],
		typicalFlows: agent.typicalFlows || [],
		blacklistedCallerNumbers: agent.blacklistedCallerNumbers || [],
		testerCallerNumbers: agent.testerCallerNumbers || [],
		transferToHumanPhoneNumber: agent.transferToHumanPhoneNumber || null,
		transferToHumanDestinations: getAgentTransferDestinations(agent),
		dtmfCollectionTools: {
			askForPhoneNumber: agent.dtmfCollectionTools?.askForPhoneNumber === true,
			askForLicenseNumber:
				agent.dtmfCollectionTools?.askForLicenseNumber === true,
			askForIdNumber: agent.dtmfCollectionTools?.askForIdNumber === true,
		},
		dos: agent.dos || "",
		donts: agent.donts || "",
		gender: agent.gender || "neutral",
		voice: isVoiceSupportedForModel(
			agent.voice,
			agent.realtime?.provider,
			agent.realtime?.model,
		)
			? agent.voice!
			: getDefaultVoiceForModel(
					agent.realtime?.provider,
					agent.realtime?.model,
				),
		tone: agent.tone || "conversational",
	};
}

export function withInboundAnsweringPolicy(
	agent: Agent,
	policy: InboundAnsweringPolicy,
): Agent {
	return {
		...agent,
		status: agent.status === "Draft" ? "Draft" : "Active",
		inboundAnsweringPolicy: policy,
	};
}

function prepareAgentForSave(agent: Agent): Agent {
	return {
		...agent,
		messages: (agent.messages || []).filter(
			(message) => message.name || message.text || message.description,
		),
		policySections: (agent.policySections || []).filter(
			(section) => section.title || section.content,
		),
		typicalFlows: (agent.typicalFlows || []).filter(
			(flow) => flow.name || flow.description || flow.example,
		),
	};
}

function createEmptyAgent(companyId: string, language: "en" | "he"): Agent {
	return {
		id: shortUuid("agent_"),
		name: "",
		companyId,
		status: "Draft",
		inboundAnsweringPolicy: "AI_FIRST",
		phoneNumbers: [],
		blacklistedCallerNumbers: [],
		testerCallerNumbers: [],
		transferToHumanPhoneNumber: null,
		transferToHumanPhoneNumbers: [],
		transferToHumanDestinations: [],
		dtmfCollectionTools: {
			askForPhoneNumber: false,
			askForLicenseNumber: false,
			askForIdNumber: false,
		},
		version: 1,
		greeting: "",
		goal: "",
		referenceMaterials: [],
		stats: {
			todayDateKey: formatLocalDateKey(new Date()),
			callsToday: 0,
			minutesToday: 0,
			totalCalls: 0,
			totalMinutes: 0,
			goalsToday: 0,
			totalGoals: 0,
		},
		serviceId: null,
		service: null,
		gender: "neutral",
		tone: "conversational",
		voice: "alloy",
		realtime: {
			provider: "openai",
			model: "gpt-realtime",
			effort: "low",
		},
		gptLive: {
			enabled: false,
			voice: "marin",
			reasoningModel: "gpt-5.6-luna",
		},
		defaultLanguage: language === "he" ? "he" : "en",
		timeZone: "Asia/Jerusalem",
		dos: "",
		donts: "",
		messagesSenderName: "",
		policySections: [],
		messages: [],
		typicalFlows: [],
	};
}

function deploymentVersion(version?: number | string | null): number {
	if (typeof version === "number" && Number.isFinite(version)) return version;
	if (typeof version === "string") {
		const normalized = version.trim().match(/\d+/)?.[0];
		const parsed = normalized ? parseInt(normalized, 10) : Number.NaN;
		return Number.isFinite(parsed) ? parsed : 0;
	}
	return 0;
}

function normalizeDeploymentStatus(
	status?: string,
): DeploymentStatus | undefined {
	if (!status) return undefined;
	return status.toLowerCase() as DeploymentStatus;
}

function getInProgressDeploymentLabel(
	status: DeploymentStatus | undefined,
	options: {
		creating: string;
		opened: string;
		validating: string;
		merged: string;
		building: string;
		deploying: string;
	},
): string | undefined {
	if (!status) return undefined;
	if (!(status in IN_PROGRESS_DEPLOYMENT_STATUS_LABELS)) return undefined;

	switch (status) {
		case "creating_pr":
			return options.creating;
		case "pr_opened":
			return options.opened;
		case "pr_validation_started":
			return options.validating;
		case "pr_merged":
			return options.merged;
		case "merge_success":
			return options.building;
		case "deploy_started":
		case "started":
			return options.deploying;
		default:
			return undefined;
	}
}

function createPendingDeploymentState(
	deployment: Agent["stagingDeployment"] | undefined,
	options?: {
		action?: "deploy" | "rollback";
		rollbackToVersion?: number;
	},
): PendingDeploymentState {
	return {
		previousDeploymentId: deployment?.id,
		previousCreatedAt: deployment?.createdAt,
		previousStatus: deployment?.status,
		previousUpdatedAt: deployment?.updatedAt,
		previousVersion: deployment?.version,
		started: false,
		action: options?.action,
		rollbackToVersion: options?.rollbackToVersion,
	};
}

function hasDeploymentChanged(
	current: Agent["stagingDeployment"] | undefined,
	pending: PendingDeploymentState,
): boolean {
	return (
		current?.id !== pending.previousDeploymentId ||
		current?.createdAt !== pending.previousCreatedAt ||
		current?.status !== pending.previousStatus ||
		current?.updatedAt !== pending.previousUpdatedAt ||
		current?.version !== pending.previousVersion
	);
}

function getOptimisticPendingDeployment(
	current: Agent["stagingDeployment"] | undefined,
	pending: PendingDeploymentState | undefined,
	fallbackVersion?: number,
): Agent["stagingDeployment"] | undefined {
	if (!pending) return current;
	if (hasDeploymentChanged(current, pending)) {
		if (!current) return current;

		return {
			...current,
			action: pending.action === "rollback" ? "rollback" : undefined,
			rollbackToVersion:
				pending.action === "rollback" ? pending.rollbackToVersion : undefined,
		};
	}

	return {
		...current,
		id: current?.id ?? "",
		status: "creating_pr",
		updatedAt: Date.now(),
		action: pending.action ?? current?.action,
		rollbackToVersion: pending.rollbackToVersion ?? current?.rollbackToVersion,
		version: pending.rollbackToVersion ?? current?.version ?? fallbackVersion,
	};
}

function isTerminalDeploymentStatus(status?: DeploymentStatus): boolean {
	return status !== undefined && TERMINAL_DEPLOYMENT_STATUSES.has(status);
}

function isDeploymentStale(
	deployment: Agent["stagingDeployment"] | undefined,
	now: number,
): boolean {
	if (!deployment?.updatedAt) return false;
	return now - deployment.updatedAt > STALE_DEPLOYMENT_MS;
}

function isDeploymentRunning(
	deployment: Agent["stagingDeployment"] | undefined,
	now: number,
): boolean {
	const status = normalizeDeploymentStatus(deployment?.status);
	if (status === undefined || !RUNNING_DEPLOYMENT_STATUSES.has(status))
		return false;
	return !isDeploymentStale(deployment, now);
}

export function getChannelStatus(
	deployment: Agent["stagingDeployment"],
	now: number,
) {
	if (!deployment) return "notDeployed";
	const status = normalizeDeploymentStatus(deployment.status);
	if (status === "deploy_success") return "active";
	if (status && FAILURE_DEPLOYMENT_STATUSES.has(status)) return "failed";
	if (isDeploymentStale(deployment, now)) return "stalled";
	return deployment.action === "rollback" ? "rollingBack" : "deploying";
}

const CHANNEL_STATUS_COLORS = {
	active: "border-live/30 bg-live-tint text-live",
	deploying: "border-ring/30 bg-accent text-accent-ink",
	rollingBack: "border-ring/30 bg-accent text-accent-ink",
	failed: "border-destructive/30 bg-danger-tint text-danger-ink",
	stalled: "border-warn/30 bg-warn-tint text-foreground",
	notDeployed: "border-border bg-secondary text-muted-foreground",
};

function currentSuccessfulLiveVersion(agent: Agent): number {
	const liveDeploymentStatus = normalizeDeploymentStatus(
		agent.liveDeployment?.status,
	);

	if (liveDeploymentStatus === "deploy_success") {
		return deploymentVersion(agent.liveDeployment?.version);
	}

	return Math.max(
		0,
		...(agent.liveDeploymentVersions || []).map((version) =>
			deploymentVersion(version),
		),
	);
}

function canPromoteToTesting(agent: Agent): boolean {
	return (
		!!agent.hasUncommittedChanges ||
		agent.version > deploymentVersion(agent.stagingDeployment?.version)
	);
}

function canPromoteToLive(agent: Agent): boolean {
	return (
		deploymentVersion(agent.stagingDeployment?.version) >
		currentSuccessfulLiveVersion(agent)
	);
}

function canRollbackLive(agent: Agent): boolean {
	const currentLiveVersion = currentSuccessfulLiveVersion(agent);
	return (agent.liveDeploymentVersions || []).some(
		(version) => deploymentVersion(version) < currentLiveVersion,
	);
}

function getRollbackTargetVersion(agent: Agent): number | undefined {
	const currentLiveVersion = currentSuccessfulLiveVersion(agent);
	const candidateVersions = (agent.liveDeploymentVersions || [])
		.map((version) => deploymentVersion(version))
		.filter(
			(version) => Number.isFinite(version) && version < currentLiveVersion,
		);

	if (candidateVersions.length === 0) return undefined;
	return Math.max(...candidateVersions);
}

function isDraftModified(agent: Agent): boolean {
	return (
		!!agent.hasUncommittedChanges ||
		deploymentVersion(agent.version) >
			deploymentVersion(agent.stagingDeployment?.version)
	);
}

function timestampMillis(value: Date | string | number | null | undefined) {
	if (value instanceof Date) return value.getTime();
	if (typeof value === "number") return value;
	if (typeof value !== "string") return null;
	const parsed = new Date(value).getTime();
	return Number.isNaN(parsed) ? null : parsed;
}

export function hasLinkedServiceChangesSinceTestingDeployment(
	agent: Agent,
): boolean {
	if (!agent.serviceId || !agent.service) return false;
	const serviceUpdatedAt = timestampMillis(agent.service.updatedAt);
	if (serviceUpdatedAt === null) return false;

	const deployment = agent.stagingDeployment;
	if (!deployment) return true;
	if (deployment.serviceId && deployment.serviceId !== agent.serviceId) {
		return true;
	}

	const deployedServiceUpdatedAt =
		deployment.serviceUpdatedAt ??
		(normalizeDeploymentStatus(deployment.status) === "deploy_success"
			? deployment.createdAt
			: null);
	return (
		deployedServiceUpdatedAt !== null &&
		deployedServiceUpdatedAt !== undefined &&
		serviceUpdatedAt > deployedServiceUpdatedAt
	);
}

export function shouldShowTestingDeploymentNotice(agent: Agent): boolean {
	return (
		agent.status === "Draft" ||
		isDraftModified(agent) ||
		hasLinkedServiceChangesSinceTestingDeployment(agent)
	);
}

export function shouldShowLiveDeploymentNotice(agent: Agent): boolean {
	return (
		normalizeDeploymentStatus(agent.stagingDeployment?.status) ===
			"deploy_success" && canPromoteToLive(agent)
	);
}

function openUrl(url: string) {
	window.open(url, "_blank", "noopener,noreferrer");
}

interface DeploymentStatusBadgeProps {
	deployment: Agent["stagingDeployment"];
	displayLabel: string;
	details: string;
	colorClassName: string;
	isSystemAdmin: boolean;
}

export function DeploymentStatusBadge({
	deployment,
	displayLabel,
	details,
	colorClassName,
	isSystemAdmin,
}: DeploymentStatusBadgeProps) {
	const hasPrUrl = !!deployment?.prUrl;
	const hasActionUrl = !!deployment?.actionUrl;
	const isClickable = isSystemAdmin && (hasPrUrl || hasActionUrl);
	const badge = (
		<Badge
			variant="outline"
			title={details}
			className={`${colorClassName} ${isClickable ? "cursor-pointer hover:brightness-95" : ""}`}
		>
			{displayLabel}
		</Badge>
	);

	if (!deployment || !isSystemAdmin || (!hasPrUrl && !hasActionUrl)) {
		return badge;
	}

	if (hasPrUrl && hasActionUrl) {
		return (
			<DropdownMenu>
				<DropdownMenuTrigger asChild>{badge}</DropdownMenuTrigger>
				<DropdownMenuContent align="start">
					<DropdownMenuItem onClick={() => openUrl(deployment.prUrl!)}>
						PR
						<ExternalLink className="ml-auto w-4 h-4" />
					</DropdownMenuItem>
					<DropdownMenuItem onClick={() => openUrl(deployment.actionUrl!)}>
						Action
						<ExternalLink className="ml-auto w-4 h-4" />
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		);
	}

	return (
		<button
			type="button"
			className="inline-flex"
			onClick={() => openUrl((deployment.prUrl || deployment.actionUrl)!)}
		>
			{badge}
		</button>
	);
}

export function formatDeploymentVersionLabel(
	deployment: Agent["stagingDeployment"],
	label: string,
): string {
	const version =
		deployment?.action === "rollback"
			? (deployment.rollbackToVersion ?? deployment.version)
			: deployment?.version;
	return typeof version === "number" && !label.includes(`v${version}`)
		? `${label} · v${version}`
		: label;
}

function getDeploymentBadgeLabel(
	i18n: LinguiI18n,
	deployment: Agent["stagingDeployment"],
	environment: "staging" | "live",
	language: "en" | "he",
	now: number,
): string {
	if (!deployment) return "";
	const isHebrew = language === "he";

	const status = normalizeDeploymentStatus(deployment.status);
	const isRollback = deployment.action === "rollback";
	const targetVersion =
		typeof deployment.rollbackToVersion === "number"
			? deployment.rollbackToVersion
			: deployment.version;

	if (environment === "staging") {
		if (
			!isTerminalDeploymentStatus(status) &&
			deployment.updatedAt &&
			now - deployment.updatedAt > STALE_DEPLOYMENT_MS
		) {
			return i18n._("agentsPage.deployment.staging.stalled");
		}
		const stagingInProgressLabel = getInProgressDeploymentLabel(status, {
			creating: i18n._("agentsPage.deployment.staging.creating"),
			opened: i18n._("agentsPage.deployment.staging.opened"),
			validating: i18n._("agentsPage.deployment.staging.validating"),
			merged: i18n._("agentsPage.deployment.staging.merged"),
			building: i18n._("agentsPage.deployment.staging.building"),
			deploying: i18n._("agentsPage.deployment.staging.deploying"),
		});
		if (stagingInProgressLabel) return stagingInProgressLabel;
		if (status === "deploy_success")
			return `${i18n._("agentsPage.deployment.staging.prefix")} v${deployment.version}`;
		if (status === "deploy_error" || status === "error")
			return i18n._("agentsPage.deployment.staging.error");
		if (status === "merge_error")
			return i18n._("agentsPage.deployment.staging.mergeError");
		return `${i18n._("agentsPage.deployment.staging.prefix")} ${deployment.status || ""}`.trim();
	}

	if (isRollback) {
		if (
			!isTerminalDeploymentStatus(status) &&
			deployment.updatedAt &&
			now - deployment.updatedAt > STALE_DEPLOYMENT_MS
		) {
			return `${i18n._("agentsPage.deployment.rollback.stalled")}${targetVersion ? ` (v${targetVersion})` : ""}`;
		}
		const rollbackInProgressLabel = getInProgressDeploymentLabel(status, {
			creating: `${i18n._("agentsPage.deployment.rollback.creating")}${targetVersion ? (isHebrew ? ` ל-v${targetVersion}` : ` to v${targetVersion}`) : ""}...`,
			opened: `${i18n._("agentsPage.deployment.rollback.opened")}${targetVersion ? (isHebrew ? ` ל-v${targetVersion}` : ` to v${targetVersion}`) : ""}...`,
			validating: `${i18n._("agentsPage.deployment.rollback.validating")}${targetVersion ? (isHebrew ? ` ל-v${targetVersion}` : ` to v${targetVersion}`) : ""}...`,
			merged: `${i18n._("agentsPage.deployment.rollback.merged")}${targetVersion ? (isHebrew ? ` ל-v${targetVersion}` : ` to v${targetVersion}`) : ""}...`,
			building: `${i18n._("agentsPage.deployment.rollback.building")}${targetVersion ? (isHebrew ? ` ל-v${targetVersion}` : ` to v${targetVersion}`) : ""}...`,
			deploying: `${i18n._("agentsPage.deployment.rollback.deploying")}${targetVersion ? (isHebrew ? ` ל-v${targetVersion}` : ` to v${targetVersion}`) : ""}...`,
		});
		if (rollbackInProgressLabel) return rollbackInProgressLabel;
		if (status === "deploy_success")
			return `${i18n._("agentsPage.deployment.live.prefix")} v${deployment.version}`;
		if (status === "deploy_error" || status === "error")
			return `${i18n._("agentsPage.deployment.rollback.error")}${targetVersion ? ` (v${targetVersion})` : ""}`;
		if (status === "merge_error")
			return `${i18n._("agentsPage.deployment.rollback.mergeError")}${targetVersion ? ` (v${targetVersion})` : ""}`;
	}

	if (
		!isTerminalDeploymentStatus(status) &&
		deployment.updatedAt &&
		now - deployment.updatedAt > STALE_DEPLOYMENT_MS
	) {
		return i18n._("agentsPage.deployment.live.stalled");
	}
	const liveInProgressLabel = getInProgressDeploymentLabel(status, {
		creating: i18n._("agentsPage.deployment.live.creating"),
		opened: i18n._("agentsPage.deployment.live.opened"),
		validating: i18n._("agentsPage.deployment.live.validating"),
		merged: i18n._("agentsPage.deployment.live.merged"),
		building: i18n._("agentsPage.deployment.live.building"),
		deploying: i18n._("agentsPage.deployment.live.deploying"),
	});
	if (liveInProgressLabel) return liveInProgressLabel;
	if (status === "deploy_success")
		return `${i18n._("agentsPage.deployment.live.prefix")} v${deployment.version}`;
	if (status === "deploy_error" || status === "error")
		return i18n._("agentsPage.deployment.live.error");
	if (status === "merge_error")
		return i18n._("agentsPage.deployment.live.mergeError");
	return `${i18n._("agentsPage.deployment.live.prefix")} ${deployment.status || ""}`.trim();
}

export function AgentsPage({
	initialAgentName,
	onAgentChange,
}: AgentsPageProps = {}) {
	const { i18n } = useLingui();
	const {
		user,
		agents,
		testingPhoneNumbers,
		calls,
		language,
		createAgent: createAgentData,
		updateAgent: updateAgentData,
		deleteAgent: deleteAgentData,
		addPhoneNumber: addPhoneNumberData,
		removePhoneNumber: removePhoneNumberData,
		promoteToTesting: promoteToTestingData,
		promoteToLive: promoteToLiveData,
		rollbackLive: rollbackLiveData,
		previewGenerateAgentFiles: previewGenerateAgentFilesData,
	} = useData();
	const isHebrew = language === "he";
	const isRtl = language === "he";
	const canManageAgents = isAdminRole(user?.role);
	const localizedStatus = (status: Agent["status"]) => {
		switch (status) {
			case "Active":
				return t({ id: "Active", message: "Active" });
			case "Draft":
				return t({ id: "Draft", message: "Draft" });
			default:
				return status;
		}
	};
	const localizedAnsweringPolicy = useCallback(
		(policy: InboundAnsweringPolicy) => {
			switch (policy) {
				case "AI_ONLY":
					return i18n._({ id: "answeringPolicy.aiOnly", message: "AI only" });
				case "HUMAN_FIRST":
					return i18n._({
						id: "answeringPolicy.humanFirst",
						message: "Human first",
					});
				case "HUMAN_ONLY":
					return i18n._({
						id: "answeringPolicy.humanOnly",
						message: "Human only",
					});
				case "AI_FIRST":
				default:
					return i18n._({ id: "answeringPolicy.aiFirst", message: "AI first" });
			}
		},
		[i18n],
	);

	const [searchQuery, setSearchQuery] = useState("");
	const [highlightedAgentId, setHighlightedAgentId] = useState<string | null>(
		null,
	);
	const [agentToDelete, setAgentToDelete] = useState<Agent | null>(null);
	const [phoneToRemove, setPhoneToRemove] = useState<{
		agent: Agent;
		phone: string;
	} | null>(null);
	const [phoneDialogAgent, setPhoneDialogAgent] = useState<Agent | null>(null);
	const [phoneInput, setPhoneInput] = useState("");
	const [editorMode, setEditorMode] = useState<"create" | "edit" | null>(null);
	const [editorAgent, setEditorAgent] = useState<Agent | null>(null);
	const [editorInitialAgent, setEditorInitialAgent] = useState<Agent | null>(
		null,
	);
	const [isSavingEditor, setIsSavingEditor] = useState(false);
	const [promotingAgents, setPromotingAgents] = useState<
		Record<string, PendingDeploymentState>
	>({});
	const [promotingLiveAgents, setPromotingLiveAgents] = useState<
		Record<string, PendingDeploymentState>
	>({});
	const [rollingBackAgents, setRollingBackAgents] = useState<
		Record<string, PendingDeploymentState>
	>({});
	const [previewingAgents, setPreviewingAgents] = useState<
		Record<string, boolean>
	>({});
	const [previewFilesState, setPreviewFilesState] =
		useState<PreviewFilesState | null>(null);
	const [updatingPolicies, setUpdatingPolicies] = useState<
		Record<string, boolean>
	>({});
	const [selectedPreviewFilePath, setSelectedPreviewFilePath] = useState<
		string | null
	>(null);

	const [, refreshDeploymentClock] = useState(0);
	const currentTime = Date.now();
	const totalCallsToday = useMemo(() => {
		const today = formatLocalDateKey(new Date());
		return calls.filter((call) => {
			const occurredAt = call.occurredAt;
			return occurredAt instanceof Date
				? formatLocalDateKey(occurredAt) === today
				: call.date === today;
		}).length;
	}, [calls]);

	useEffect(() => {
		const now = Date.now();
		let nextStaleAt: number | undefined;

		for (const agent of agents) {
			for (const deployment of [
				agent.stagingDeployment,
				agent.liveDeployment,
			]) {
				const status = normalizeDeploymentStatus(deployment?.status);
				if (!deployment?.updatedAt || isTerminalDeploymentStatus(status))
					continue;

				const staleAt = deployment.updatedAt + STALE_DEPLOYMENT_MS + 1;
				if (staleAt <= now) continue;
				nextStaleAt = Math.min(nextStaleAt ?? staleAt, staleAt);
			}
		}

		if (nextStaleAt === undefined) return;

		const timeout = window.setTimeout(
			() => {
				refreshDeploymentClock((tick) => tick + 1);
			},
			Math.max(0, nextStaleAt - now),
		);

		return () => window.clearTimeout(timeout);
	});

	const agentRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
	const agentsRef = useRef<Agent[]>(agents);

	useEffect(() => {
		agentsRef.current = agents;
	}, [agents]);

	useEffect(() => {
		setPromotingAgents((prev) => {
			const next = { ...prev };
			let changed = false;

			for (const agent of agents) {
				const pending = next[agent.id];
				if (!pending) continue;
				const status = (
					agent.stagingDeployment?.status || ""
				).toLowerCase() as DeploymentStatus;
				const deploymentChanged = hasDeploymentChanged(
					agent.stagingDeployment,
					pending,
				);

				if (deploymentChanged && TERMINAL_DEPLOYMENT_STATUSES.has(status)) {
					delete next[agent.id];
					changed = true;
					continue;
				}

				if (
					!pending.started &&
					deploymentChanged &&
					START_CONFIRMED_STATUSES.has(status)
				) {
					next[agent.id] = { ...pending, started: true };
					changed = true;
					continue;
				}

				if (pending.started && TERMINAL_DEPLOYMENT_STATUSES.has(status)) {
					delete next[agent.id];
					changed = true;
				}
			}

			return changed ? next : prev;
		});

		setPromotingLiveAgents((prev) => {
			const next = { ...prev };
			let changed = false;

			for (const agent of agents) {
				const pending = next[agent.id];
				if (!pending) continue;
				const status = (
					agent.liveDeployment?.status || ""
				).toLowerCase() as DeploymentStatus;
				const deploymentChanged = hasDeploymentChanged(
					agent.liveDeployment,
					pending,
				);

				if (deploymentChanged && TERMINAL_DEPLOYMENT_STATUSES.has(status)) {
					delete next[agent.id];
					changed = true;
					continue;
				}

				if (
					!pending.started &&
					deploymentChanged &&
					START_CONFIRMED_STATUSES.has(status)
				) {
					next[agent.id] = { ...pending, started: true };
					changed = true;
					continue;
				}

				if (pending.started && TERMINAL_DEPLOYMENT_STATUSES.has(status)) {
					delete next[agent.id];
					changed = true;
				}
			}

			return changed ? next : prev;
		});

		setRollingBackAgents((prev) => {
			const next = { ...prev };
			let changed = false;

			for (const agent of agents) {
				const pending = next[agent.id];
				if (!pending) continue;
				const status = (
					agent.liveDeployment?.status || ""
				).toLowerCase() as DeploymentStatus;
				const deploymentChanged = hasDeploymentChanged(
					agent.liveDeployment,
					pending,
				);

				if (deploymentChanged && TERMINAL_DEPLOYMENT_STATUSES.has(status)) {
					delete next[agent.id];
					changed = true;
					continue;
				}

				if (
					!pending.started &&
					deploymentChanged &&
					START_CONFIRMED_STATUSES.has(status)
				) {
					next[agent.id] = { ...pending, started: true };
					changed = true;
					continue;
				}

				if (pending.started && TERMINAL_DEPLOYMENT_STATUSES.has(status)) {
					delete next[agent.id];
					changed = true;
				}
			}

			return changed ? next : prev;
		});
	}, [agents]);

	useEffect(() => {
		if (!initialAgentName) return;
		const agent = agents.find((item) => item.name === initialAgentName);
		if (!agent) return;

		setTimeout(() => {
			const element = agentRefs.current[agent.id];
			if (element) {
				element.scrollIntoView({ behavior: "smooth", block: "center" });
				setHighlightedAgentId(agent.id);
				setTimeout(() => {
					setHighlightedAgentId(null);
					onAgentChange?.();
				}, 2000);
			}
		}, 100);
	}, [initialAgentName, agents, onAgentChange]);

	const filteredAgents = useMemo(() => {
		const query = searchQuery.trim().toLowerCase();
		if (!query) return agents;

		return agents.filter((agent) => {
			const policy = getAgentInboundAnsweringPolicy(agent);
			return (
				agent.name.toLowerCase().includes(query) ||
				agent.status.toLowerCase().includes(query) ||
				policy.toLowerCase().includes(query) ||
				localizedAnsweringPolicy(policy).toLowerCase().includes(query) ||
				agent.phoneNumbers.some((phone) => phone.toLowerCase().includes(query))
			);
		});
	}, [agents, searchQuery, localizedAnsweringPolicy]);

	const openCreateEditor = () => {
		if (!canManageAgents) return;
		if (!user?.companyId) {
			toast.error(i18n._("agentsPage.missingCompany"));
			return;
		}
		const emptyAgent = createEmptyAgent(user.companyId, language);
		setEditorMode("create");
		setEditorAgent(emptyAgent);
		setEditorInitialAgent(emptyAgent);
	};

	const openEditEditor = (agent: Agent) => {
		if (!canManageAgents) return;
		const normalized = normalizeAgentForEditor({ ...agent });
		setEditorMode("edit");
		setEditorAgent(normalized);
		setEditorInitialAgent(normalized);
	};

	const closeEditor = () => {
		setEditorMode(null);
		setEditorAgent(null);
		setEditorInitialAgent(null);
		setIsSavingEditor(false);
	};

	const closePreviewFiles = () => {
		setPreviewFilesState(null);
		setSelectedPreviewFilePath(null);
	};

	const hasNewerVersion = useMemo(() => {
		if (editorMode !== "edit" || !editorInitialAgent) return false;
		const serverAgent = agents.find((a) => a.id === editorInitialAgent.id);
		if (!serverAgent || !serverAgent.updatedAt || !editorInitialAgent.updatedAt)
			return false;

		const serverTime = toEditorTimestampMillis(serverAgent.updatedAt);
		const initialTime = toEditorTimestampMillis(editorInitialAgent.updatedAt);
		if (serverTime === null || initialTime === null) return false;

		return serverTime > initialTime;
	}, [agents, editorMode, editorInitialAgent]);

	const isEditorDirty = useMemo(() => {
		if (!editorAgent || !editorInitialAgent) return false;
		const comparable = (agent: Agent) => {
			const prepared = prepareAgentForSave(agent);
			const persisted = { ...prepared };
			delete persisted.stagingDeployment;
			delete persisted.liveDeployment;
			delete persisted.liveDeploymentVersions;
			delete persisted.hasUncommittedChanges;
			return persisted;
		};
		return (
			JSON.stringify(comparable(editorAgent)) !==
			JSON.stringify(comparable(editorInitialAgent))
		);
	}, [editorAgent, editorInitialAgent]);

	const saveEditor = async () => {
		if (!canManageAgents) return;
		if (!editorAgent || !editorMode) return;
		if (!isEditorDirty) return;
		if (isSavingEditor) return;
		const transferToHumanDestinations =
			getAgentTransferDestinations(editorAgent);
		const hasInvalidSchedule = transferToHumanDestinations.some(
			(destination) =>
				destination.availability.mode === "custom" &&
				(destination.availability.windows.length === 0 ||
					destination.availability.windows.some(
						(window) =>
							window.days.length === 0 || window.endTime <= window.startTime,
					)),
		);
		if (hasInvalidSchedule) {
			toast.error(
				i18n._({
					id: "agentsPage.invalidTransferSchedule",
					message:
						"Each custom availability window needs at least one day and an end time later than its start time.",
				}),
			);
			return;
		}
		const transferTargets = normalizeTransferTargetListForStorage(
			transferToHumanDestinations.map((destination) => destination.address),
		);
		const payload = {
			...prepareAgentForSave(editorAgent),
			blacklistedCallerNumbers: normalizePhoneNumberListForStorage(
				editorAgent.blacklistedCallerNumbers || [],
			),
			testerCallerNumbers: normalizePhoneNumberListForStorage(
				editorAgent.testerCallerNumbers || [],
			),
			transferToHumanDestinations,
			transferToHumanPhoneNumbers: transferTargets,
			transferToHumanPhoneNumber: transferTargets[0] || null,
		};

		if (!payload.name.trim()) {
			toast.error(i18n._("agentsPage.agentNameRequired"));
			return;
		}

		setIsSavingEditor(true);
		try {
			if (editorMode === "create") {
				await createAgentData(payload);
				toast.success(i18n._("agentsPage.agentCreated"));
			} else {
				await updateAgentData(payload);
				toast.success(i18n._("agentsPage.agentUpdated"));
			}
			closeEditor();
		} catch (error) {
			console.error("Failed to save agent:", error);
			toast.error(
				error instanceof Error
					? error.message
					: i18n._("agentsPage.saveFailed"),
			);
		} finally {
			setIsSavingEditor(false);
		}
	};

	const handleDeleteAgent = async (agent: Agent) => {
		if (!canManageAgents) return;
		await deleteAgentData(agent.id);
		setAgentToDelete(null);
		toast.success(`${agent.name} ${i18n._("agentsPage.deletedSuffix")}`);
	};

	const waitForDeploymentStart = async (
		agentId: string,
		target: "staging" | "live",
		previousDeployment: Agent["stagingDeployment"] | undefined,
		timeoutMs = 5000,
	): Promise<boolean> => {
		const deadline = Date.now() + timeoutMs;
		while (Date.now() < deadline) {
			const agent = agentsRef.current.find((current) => current.id === agentId);
			const deployment =
				target === "staging" ? agent?.stagingDeployment : agent?.liveDeployment;
			const deploymentStatus = (
				deployment?.status || ""
			).toLowerCase() as DeploymentStatus;
			const deploymentChanged =
				!previousDeployment ||
				deployment?.id !== previousDeployment.id ||
				deployment?.status !== previousDeployment.status ||
				deployment?.updatedAt !== previousDeployment.updatedAt ||
				deployment?.version !== previousDeployment.version;

			if (deploymentChanged && START_CONFIRMED_STATUSES.has(deploymentStatus)) {
				return true;
			}

			await new Promise((resolve) => setTimeout(resolve, 250));
		}

		return false;
	};

	const handleForceRedeployStaging = async (agent: Agent) => {
		if (!canManageAgents) return;
		const previousDeployment = agent.stagingDeployment;
		try {
			setPromotingAgents((prev) => ({
				...prev,
				[agent.id]: createPendingDeploymentState(previousDeployment, {
					action: "deploy",
				}),
			}));
			await promoteToTestingData(agent.id);
			toast.success(
				`${i18n._("agentsPage.toast.deployStartedTesting")} ${agent.name} ${i18n._("agentsPage.toast.deployStartedTestingSuffix")}`,
			);
		} catch (error) {
			const deploymentStarted = await waitForDeploymentStart(
				agent.id,
				"staging",
				previousDeployment,
			);
			if (deploymentStarted) {
				toast.success(
					`${i18n._("agentsPage.toast.deployStartedTesting")} ${agent.name} ${i18n._("agentsPage.toast.deployStartedTestingSuffix")}`,
				);
				return;
			}
			console.error("Force Ready to Test failed", error);
			if (isGitHubUnavailableError(error)) {
				toast.error(i18n._("agentsPage.toast.githubUnavailable"));
			} else {
				toast.error(
					`${i18n._("agentsPage.toast.forceDeployTestingFailed")} ${agent.name} ${i18n._("agentsPage.toast.forceDeployTestingSuffix")}`,
				);
			}
			setPromotingAgents((prev) => {
				const next = { ...prev };
				delete next[agent.id];
				return next;
			});
		}
	};

	const handleForceRedeployLive = async (agent: Agent) => {
		if (!canManageAgents) return;
		const previousDeployment = agent.liveDeployment;
		try {
			setPromotingLiveAgents((prev) => ({
				...prev,
				[agent.id]: createPendingDeploymentState(previousDeployment, {
					action: "deploy",
				}),
			}));
			await promoteToLiveData(agent.id);
			toast.success(
				`${i18n._("agentsPage.toast.deployStartedLive")} ${agent.name} ${i18n._("agentsPage.toast.deployStartedLiveSuffix")}`,
			);
		} catch (error) {
			const deploymentStarted = await waitForDeploymentStart(
				agent.id,
				"live",
				previousDeployment,
			);
			if (deploymentStarted) {
				toast.success(
					`${i18n._("agentsPage.toast.deployStartedLive")} ${agent.name} ${i18n._("agentsPage.toast.deployStartedLiveSuffix")}`,
				);
				return;
			}
			console.error("Force Go Live failed", error);
			if (isGitHubUnavailableError(error)) {
				toast.error(i18n._("agentsPage.toast.githubUnavailable"));
			} else {
				toast.error(
					`${i18n._("agentsPage.toast.forceDeployLiveFailed")} ${agent.name} ${i18n._("agentsPage.toast.forceDeployLiveSuffix")}`,
				);
			}
			setPromotingLiveAgents((prev) => {
				const next = { ...prev };
				delete next[agent.id];
				return next;
			});
		}
	};

	const handlePromoteToTesting = async (agent: Agent) => {
		if (!canManageAgents) return;
		if (!canPromoteToTesting(agent)) {
			await handleForceRedeployStaging(agent);
			return;
		}

		const previousDeployment = agent.stagingDeployment;
		try {
			setPromotingAgents((prev) => ({
				...prev,
				[agent.id]: createPendingDeploymentState(previousDeployment, {
					action: "deploy",
				}),
			}));
			await promoteToTestingData(agent.id);
			toast.success(
				`${i18n._("agentsPage.toast.deployStartedTesting")} ${agent.name} ${i18n._("agentsPage.toast.deployStartedTestingSuffix")}`,
			);
		} catch (error) {
			const deploymentStarted = await waitForDeploymentStart(
				agent.id,
				"staging",
				previousDeployment,
			);
			if (deploymentStarted) {
				toast.success(
					`${i18n._("agentsPage.toast.deployStartedTesting")} ${agent.name} ${i18n._("agentsPage.toast.deployStartedTestingSuffix")}`,
				);
				return;
			}
			console.error("Ready to Test failed", error);
			if (isGitHubUnavailableError(error)) {
				toast.error(i18n._("agentsPage.toast.githubUnavailable"));
			} else {
				toast.error(
					`${i18n._("agentsPage.toast.deployTestingFailed")} ${agent.name} ${i18n._("agentsPage.toast.deployTestingSuffix")}`,
				);
			}
			setPromotingAgents((prev) => {
				const next = { ...prev };
				delete next[agent.id];
				return next;
			});
		}
	};

	const handlePromoteToLive = async (agent: Agent) => {
		if (!canManageAgents) return;
		if (!canPromoteToLive(agent)) {
			await handleForceRedeployLive(agent);
			return;
		}

		const previousDeployment = agent.liveDeployment;
		try {
			setPromotingLiveAgents((prev) => ({
				...prev,
				[agent.id]: createPendingDeploymentState(previousDeployment, {
					action: "deploy",
				}),
			}));
			await promoteToLiveData(agent.id);
			toast.success(
				`${i18n._("agentsPage.toast.deployStartedLive")} ${agent.name} ${i18n._("agentsPage.toast.deployStartedLiveSuffix")}`,
			);
		} catch (error) {
			const deploymentStarted = await waitForDeploymentStart(
				agent.id,
				"live",
				previousDeployment,
			);
			if (deploymentStarted) {
				toast.success(
					`${i18n._("agentsPage.toast.deployStartedLive")} ${agent.name} ${i18n._("agentsPage.toast.deployStartedLiveSuffix")}`,
				);
				return;
			}
			console.error("Go Live failed", error);
			if (isGitHubUnavailableError(error)) {
				toast.error(i18n._("agentsPage.toast.githubUnavailable"));
			} else {
				toast.error(
					`${i18n._("agentsPage.toast.deployLiveFailed")} ${agent.name} ${i18n._("agentsPage.toast.deployLiveSuffix")}`,
				);
			}
			setPromotingLiveAgents((prev) => {
				const next = { ...prev };
				delete next[agent.id];
				return next;
			});
		}
	};

	const handleRollbackLive = async (agent: Agent) => {
		if (!canManageAgents) return;
		if (!canRollbackLive(agent)) return;
		const previousVersion = getRollbackTargetVersion(agent);
		if (previousVersion === undefined) {
			toast.info(i18n._("agentsPage.toast.noRollback"));
			return;
		}

		try {
			setRollingBackAgents((prev) => ({
				...prev,
				[agent.id]: createPendingDeploymentState(agent.liveDeployment, {
					action: "rollback",
					rollbackToVersion: previousVersion,
				}),
			}));
			await rollbackLiveData(agent.id, previousVersion);
			toast.success(
				`${agent.name} ${i18n._("agentsPage.toast.rollbackStartedSuffix")} (v${previousVersion})`,
			);
		} catch (error) {
			console.error("Rollback Live failed", error);
			if (isGitHubUnavailableError(error)) {
				toast.error(i18n._("agentsPage.toast.githubUnavailable"));
			} else {
				toast.error(
					isHebrew
						? `${i18n._("agentsPage.toast.rollbackFailed")} ${agent.name} נכשלה`
						: `${i18n._("agentsPage.toast.rollbackFailed")} ${agent.name}`,
				);
			}
			setRollingBackAgents((prev) => {
				const next = { ...prev };
				delete next[agent.id];
				return next;
			});
		}
	};

	const handlePreviewFiles = async (agent: Agent) => {
		try {
			setPreviewingAgents((prev) => ({ ...prev, [agent.id]: true }));
			const result = await previewGenerateAgentFilesData(agent.id);
			setPreviewFilesState({
				agentId: result.agentId,
				agentName: agent.name,
				version: result.version,
				generatedAt: result.generatedAt,
				files: result.files,
			});
			setSelectedPreviewFilePath(result.files[0]?.path || null);
		} catch (error) {
			console.error("Preview Files failed", error);
			toast.error(`${i18n._("agentsPage.toast.previewFailed")} ${agent.name}`);
		} finally {
			setPreviewingAgents((prev) => ({ ...prev, [agent.id]: false }));
		}
	};

	const selectedPreviewFile = useMemo(() => {
		if (!previewFilesState || !selectedPreviewFilePath) return null;
		return (
			previewFilesState.files.find(
				(file) => file.path === selectedPreviewFilePath,
			) ||
			previewFilesState.files[0] ||
			null
		);
	}, [previewFilesState, selectedPreviewFilePath]);

	const handleCopyPreviewFile = async () => {
		if (!selectedPreviewFile) return;
		try {
			await navigator.clipboard.writeText(selectedPreviewFile.content);
			toast.success(
				`${selectedPreviewFile.path} ${i18n._("agentsPage.toast.copiedSuffix")}`,
			);
		} catch (error) {
			console.error("Copy preview file failed", error);
			toast.error(i18n._("agentsPage.toast.copyFailed"));
		}
	};

	const handleAddPhoneNumber = async () => {
		if (!phoneDialogAgent || !phoneInput.trim()) return;
		await addPhoneNumberData(phoneDialogAgent.id, phoneInput.trim());
		toast.success(i18n._("phoneNumberAddedSuccess"));
		setPhoneInput("");
		setPhoneDialogAgent(null);
	};

	const confirmRemovePhoneNumber = async () => {
		if (!phoneToRemove) return;
		await removePhoneNumberData(phoneToRemove.agent.id, phoneToRemove.phone);
		toast.success(i18n._("phoneNumberRemovedSuccess"));
		setPhoneToRemove(null);
	};

	const handleUpdateAnsweringPolicy = async (
		agent: Agent,
		policy: InboundAnsweringPolicy,
	) => {
		if (!canManageAgents) return;

		try {
			setUpdatingPolicies((current) => ({ ...current, [agent.id]: true }));
			await updateAgentData(withInboundAnsweringPolicy(agent, policy));
			toast.success(
				i18n._({
					id: "agentsPage.toast.answeringPolicyUpdated",
					message: "Inbound answering policy updated",
				}),
			);
		} catch (error) {
			console.error("Failed to update inbound answering policy", error);
			toast.error(
				i18n._({
					id: "agentsPage.toast.answeringPolicyUpdateFailed",
					message: "Failed to update inbound answering policy",
				}),
			);
		} finally {
			setUpdatingPolicies((current) => ({ ...current, [agent.id]: false }));
		}
	};

	const getStatusColor = (status: Agent["status"]) => {
		switch (status) {
			case "Active":
				return "bg-live-tint text-foreground";
			case "Draft":
			default:
				return "bg-muted text-foreground";
		}
	};

	const renderDeploymentBadge = (
		deployment: Agent["stagingDeployment"],
		environment: "staging" | "live",
	) => {
		const status = getChannelStatus(deployment, currentTime);
		const labels = {
			active: t({ id: "agentsPage.channel.active", message: "Active" }),
			deploying: t({
				id: "agentsPage.channel.deploying",
				message: "Deploying",
			}),
			rollingBack: t({
				id: "agentsPage.channel.rollingBack",
				message: "Rolling back",
			}),
			failed: t({ id: "agentsPage.channel.failed", message: "Failed" }),
			stalled: t({ id: "agentsPage.channel.stalled", message: "Stalled" }),
			notDeployed: t({
				id: "agentsPage.channel.notDeployed",
				message: "Not deployed",
			}),
		};
		const displayLabel = labels[status];
		const details = deployment
			? formatDeploymentVersionLabel(
					deployment,
					getDeploymentBadgeLabel(
						i18n,
						deployment,
						environment,
						language,
						currentTime,
					),
				)
			: displayLabel;
		const colorClassName = CHANNEL_STATUS_COLORS[status];
		return (
			<DeploymentStatusBadge
				deployment={deployment}
				displayLabel={displayLabel}
				details={details}
				colorClassName={colorClassName}
				isSystemAdmin={isSystemAdminRole(user?.role)}
			/>
		);
	};

	return (
		<div
			className="mx-auto max-w-7xl space-y-6 px-4 overflow-x-clip"
			data-testid="agents-page"
		>
			<PageHeader
				className={
					editorMode ? "hidden" : "sm:flex-row sm:items-end sm:justify-between"
				}
				title={t({ id: "agents", message: "Agents" })}
				description={t({
					id: "agentsPageSubtitle",
					message: "Manage your agents and their configurations",
				})}
				actions={
					canManageAgents && (
						<>
							<Button onClick={openCreateEditor} className="w-full sm:w-auto">
								<Plus
									className={`w-4 h-4 ${language === "he" ? "ml-2" : "mr-2"}`}
								/>
								{t({ id: "createAgent", message: "Create Agent" })}
							</Button>
						</>
					)
				}
			/>

			{!editorMode && (
				<div className="relative min-w-0 w-full">
					<Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						placeholder={t({
							id: "searchAgents",
							message: "Search agents by name, phone number, or status...",
						})}
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						{...getContentFieldProps(
							searchQuery,
							language === "he" ? "rtl" : "ltr",
							"ps-10",
						)}
					/>
				</div>
			)}

			{!editorMode && (
				<div className="overflow-hidden rounded-lg border-[1.5px] border-card-border bg-card shadow-card">
					<dl className="flex flex-wrap gap-px bg-card-border">
						{[
							{
								key: "agents",
								label: t({ id: "agents", message: "Agents" }),
								value: agents.length,
								icon: Users,
								color: "bg-accent text-accent-ink",
							},
							{
								key: "active",
								label: t({ id: "active", message: "Active" }),
								value: agents.filter((item) => item.status === "Active").length,
								icon: CheckCircle2,
								color: "bg-live-tint text-live",
							},
							{
								key: "needs-testing-deployment",
								label: t({
									id: "agentsPage.needsTestingDeployment",
									message: "Need Testing Deployment",
								}),
								value: agents.filter(shouldShowTestingDeploymentNotice).length,
								icon: Edit,
								color: "bg-secondary text-muted-foreground",
							},
							{
								key: "calls",
								label: t({
									id: "totalCallsToday_lower",
									message: "Total Calls Today",
								}),
								value: totalCallsToday,
								icon: Phone,
								color: "bg-accent text-accent-ink",
							},
						].map((metric) => {
							const Icon = metric.icon;
							return (
								<div
									key={metric.key}
									className="flex min-w-0 flex-[1_1_max-content] items-center justify-center gap-2 bg-card px-4 py-3"
								>
									<div
										className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${metric.color}`}
									>
										<Icon className="h-4 w-4" />
									</div>
									<div className="flex min-w-0 items-baseline gap-2">
										<dt className="whitespace-nowrap text-sm text-muted-foreground">
											{metric.label}
										</dt>
										<dd
											className="order-first text-lg font-bold tabular-nums"
											dir="ltr"
										>
											{metric.value.toLocaleString(language)}
										</dd>
									</div>
								</div>
							);
						})}
					</dl>
				</div>
			)}

			<div className={editorMode ? "hidden" : "space-y-4"}>
				{filteredAgents.map((agent) => {
					const answeringPolicy = getAgentInboundAnsweringPolicy(agent);
					const canReadyToTest = canPromoteToTesting(agent);
					const canGoLive = canPromoteToLive(agent);
					const canRollback = canRollbackLive(agent);
					const draftModified = isDraftModified(agent);
					const showTestingDeploymentNotice =
						shouldShowTestingDeploymentNotice(agent);
					const showLiveDeploymentNotice =
						shouldShowLiveDeploymentNotice(agent);
					const isStagingStatusRunning = isDeploymentRunning(
						agent.stagingDeployment,
						currentTime,
					);
					const isLiveStatusRunning = isDeploymentRunning(
						agent.liveDeployment,
						currentTime,
					);

					const isStagingDeploymentRunning =
						!!promotingAgents[agent.id] || isStagingStatusRunning;

					const isLiveDeployLocal = !!promotingLiveAgents[agent.id];
					const isLiveRollbackLocal = !!rollingBackAgents[agent.id];
					const isBackendRollbackRunning =
						agent.liveDeployment?.action === "rollback" && isLiveStatusRunning;
					const isBackendDeployRunning =
						!isBackendRollbackRunning && isLiveStatusRunning;

					const isLiveRollbackRunning =
						isLiveRollbackLocal ||
						(!isLiveDeployLocal &&
							!isLiveRollbackLocal &&
							isBackendRollbackRunning);

					const isLiveDeployRunning =
						isLiveDeployLocal ||
						(!isLiveRollbackLocal &&
							!isLiveDeployLocal &&
							isBackendDeployRunning);

					const effectiveStagingDeployment = getOptimisticPendingDeployment(
						agent.stagingDeployment,
						promotingAgents[agent.id],
						deploymentVersion(agent.version),
					);
					const effectiveLiveDeployment = getOptimisticPendingDeployment(
						agent.liveDeployment,
						rollingBackAgents[agent.id] ?? promotingLiveAgents[agent.id],
						deploymentVersion(agent.stagingDeployment?.version) ||
							deploymentVersion(agent.version),
					);

					const isAnyLiveOperationRunning =
						isLiveDeployRunning || isLiveRollbackRunning;
					const isAnyOperationRunning =
						isStagingDeploymentRunning || isAnyLiveOperationRunning;

					const livePhone = agent.phoneNumberDetails?.live?.length
						? formatPhoneNumberList(
								agent.phoneNumberDetails.live.map((p) => p.number),
							)
						: agent.phoneNumbers?.length
							? formatPhoneNumberList(agent.phoneNumbers)
							: "—";

					const testingPhone = hasSuccessfulTestingDeployment(agent)
						? formatPhoneNumberList(testingPhoneNumbers) || "—"
						: i18n._("agentsPage.testingPhone.deployFirst");

					return (
						<Card
							key={agent.id}
							ref={(el) => {
								agentRefs.current[agent.id] = el;
							}}
							className={`hover:shadow-card transition-all duration-500 ${
								highlightedAgentId === agent.id
									? "ring-4 ring-blue-500 shadow-2xl"
									: ""
							}`}
						>
							<CardContent className="p-0 [&:last-child]:pb-0">
								<div className="flex flex-wrap items-start gap-4 border-b border-card-border p-6">
									<div className="min-w-0 flex-[1_1_240px] space-y-2">
										<div className="flex flex-wrap items-start gap-3">
											<div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
												<CardTitle className="flex min-w-0 items-center gap-2">
													<Users className="h-5 w-5 shrink-0" />
													<span className="min-w-0 break-words">
														{agent.name}
													</span>
												</CardTitle>
												<div className="flex flex-wrap items-center gap-2">
													{agent.gptLive?.enabled && (
														<Badge
															variant="outline"
															className="border-blue-300 bg-blue-50 text-blue-800"
														>
															{i18n._({
																id: "agentsPage.badges.gptLive",
																message: "GPTLive",
															})}
															: {agent.gptLive.voice || "marin"}
														</Badge>
													)}
													<Badge
														variant="outline"
														className={
															draftModified
																? "border-warn/30 bg-warn-tint text-foreground"
																: undefined
														}
													>
														{i18n._("agentsPage.draft")}
														{draftModified ? i18n._("agentsPage.modified") : ""}
													</Badge>{" "}
													<Badge className={getStatusColor(agent.status)}>
														{localizedStatus(agent.status)}
													</Badge>
												</div>
											</div>
										</div>
										{agent.service?.name && (
											<p
												className="text-sm text-muted-foreground break-words"
												dir="auto"
											>
												{agent.service.name}
											</p>
										)}
									</div>
									<div className="ms-auto min-w-0 max-w-full shrink-0">
										{canManageAgents && (
											<Select
												value={answeringPolicy}
												onValueChange={(value: InboundAnsweringPolicy) =>
													handleUpdateAnsweringPolicy(agent, value)
												}
												disabled={
													isAnyOperationRunning || updatingPolicies[agent.id]
												}
											>
												<SelectTrigger
													className={
														isHebrew ? "w-56 max-w-full" : "w-40 max-w-full"
													}
													aria-label={i18n._({
														id: "answeringPolicy.label",
														message: "Inbound answering",
													})}
												>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													{(
														[
															"AI_FIRST",
															"AI_ONLY",
															"HUMAN_FIRST",
															"HUMAN_ONLY",
														] as const
													).map((policy) => (
														<SelectItem key={policy} value={policy}>
															{localizedAnsweringPolicy(policy)}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										)}
										{!canManageAgents && (
											<Badge variant="outline">
												{localizedAnsweringPolicy(answeringPolicy)}
											</Badge>
										)}
									</div>
								</div>
								<div className="flex flex-wrap gap-px border-b border-card-border bg-card-border">
									<div className="min-w-0 flex-[1_1_280px] space-y-3 bg-card px-6 py-4 text-sm">
										<div className="flex flex-wrap items-center justify-between gap-2">
											<h3 className="text-base font-bold text-foreground">
												{i18n._("agentEditor.phone.testing")}
											</h3>
											{showTestingDeploymentNotice && (
												<div className="rounded-lg border border-warn/30 bg-warn-tint px-3 py-1.5 text-center text-xs font-semibold text-foreground">
													{i18n._({
														id: "agentsPage.testingDeploymentNeeded",
														message: "Changes detected. Deploy to Testing.",
													})}
												</div>
											)}
										</div>
										<dl className="space-y-2">
											<div className="flex flex-wrap items-baseline gap-x-2">
												<dt className="sr-only">
													{t({
														id: "agentsPage.channel.phone",
														message: "Phone number",
													})}
												</dt>
												<dd
													className="min-w-0 break-words text-lg font-semibold tabular-nums text-foreground"
													dir="auto"
												>
													{testingPhone}
												</dd>
											</div>
											<div className="flex flex-wrap items-baseline gap-x-2">
												<dt className="text-muted-foreground">
													{t({
														id: "agentsPage.channel.version",
														message: "Deployed version",
													})}
												</dt>
												<dd className="text-muted-foreground">
													{normalizeDeploymentStatus(
														agent.stagingDeployment?.status,
													) === "deploy_success" &&
													typeof agent.stagingDeployment?.version === "number"
														? `v${agent.stagingDeployment?.action === "rollback" ? (agent.stagingDeployment.rollbackToVersion ?? agent.stagingDeployment.version) : agent.stagingDeployment?.version}`
														: agent.stagingDeployment
															? t({
																	id: "agentsPage.channel.unconfirmed",
																	message: "Not confirmed",
																})
															: i18n._("dashboard.agent.notDeployed")}
												</dd>
											</div>
										</dl>
										<div className="flex flex-wrap items-center gap-2">
											<span className="text-muted-foreground">
												{t({
													id: "agentsPage.channel.status",
													message: "Status",
												})}
											</span>
											{effectiveStagingDeployment && (
												<div className="flex items-center gap-1">
													{renderDeploymentBadge(
														effectiveStagingDeployment,
														"staging",
													)}
													{canManageAgents &&
														effectiveStagingDeployment.status &&
														!isTerminalDeploymentStatus(
															effectiveStagingDeployment.status,
														) &&
														isDeploymentStale(
															effectiveStagingDeployment,
															currentTime,
														) && (
															<Button
																variant="ghost"
																size="icon"
																className="h-6 w-6 rounded-full"
																title={i18n._(
																	"agentsPage.forceRedeployStaging",
																)}
																onClick={() =>
																	handleForceRedeployStaging(agent)
																}
																disabled={!!promotingAgents[agent.id]}
															>
																<RotateCcw
																	className={`h-3 w-3 ${promotingAgents[agent.id] ? "animate-spin" : ""}`}
																/>
															</Button>
														)}
												</div>
											)}

											{!effectiveStagingDeployment &&
												renderDeploymentBadge(undefined, "staging")}
										</div>
									</div>
									<div className="min-w-0 flex-[1_1_280px] space-y-3 bg-card px-6 py-4 text-sm">
										<div className="flex flex-wrap items-center justify-between gap-2">
											<h3 className="text-base font-bold text-foreground">
												{i18n._("agentEditor.phone.live")}
											</h3>
											{showLiveDeploymentNotice && (
												<div className="rounded-lg border border-warn/30 bg-warn-tint px-3 py-1.5 text-center text-xs font-semibold text-foreground">
													{i18n._({
														id: "agentsPage.liveDeploymentNeeded",
														message:
															"Testing is newer than Live. Deploy to Live.",
													})}
												</div>
											)}
										</div>
										<dl className="space-y-2">
											<div className="flex flex-wrap items-baseline gap-x-2">
												<dt className="sr-only">
													{t({
														id: "agentsPage.channel.phone",
														message: "Phone number",
													})}
												</dt>
												<dd
													className="min-w-0 break-words text-lg font-semibold tabular-nums text-foreground"
													dir="auto"
												>
													{livePhone}
												</dd>
											</div>
											<div className="flex flex-wrap items-baseline gap-x-2">
												<dt className="text-muted-foreground">
													{t({
														id: "agentsPage.channel.version",
														message: "Deployed version",
													})}
												</dt>
												<dd className="text-muted-foreground">
													{normalizeDeploymentStatus(
														agent.liveDeployment?.status,
													) === "deploy_success" &&
													typeof agent.liveDeployment?.version === "number"
														? `v${agent.liveDeployment?.action === "rollback" ? (agent.liveDeployment.rollbackToVersion ?? agent.liveDeployment.version) : agent.liveDeployment?.version}`
														: agent.liveDeployment
															? t({
																	id: "agentsPage.channel.unconfirmed",
																	message: "Not confirmed",
																})
															: i18n._("dashboard.agent.notDeployed")}
												</dd>
											</div>
										</dl>
										<div className="flex flex-wrap items-center gap-2">
											<span className="text-muted-foreground">
												{t({
													id: "agentsPage.channel.status",
													message: "Status",
												})}
											</span>
											{effectiveLiveDeployment && (
												<div className="flex items-center gap-1">
													{renderDeploymentBadge(
														effectiveLiveDeployment,
														"live",
													)}
													{canManageAgents &&
														effectiveLiveDeployment.status &&
														!isTerminalDeploymentStatus(
															effectiveLiveDeployment.status,
														) &&
														isDeploymentStale(
															effectiveLiveDeployment,
															currentTime,
														) && (
															<Button
																variant="ghost"
																size="icon"
																className="h-6 w-6 rounded-full"
																title={i18n._("agentsPage.forceRedeployLive")}
																onClick={() => handleForceRedeployLive(agent)}
																disabled={
																	!!promotingLiveAgents[agent.id] ||
																	!!rollingBackAgents[agent.id]
																}
															>
																<RotateCcw
																	className={`h-3 w-3 ${promotingLiveAgents[agent.id] ? "animate-spin" : ""}`}
																/>
															</Button>
														)}
												</div>
											)}

											{!effectiveLiveDeployment &&
												renderDeploymentBadge(undefined, "live")}
										</div>
									</div>
								</div>
								{canManageAgents && (
									<div className="flex flex-wrap items-center gap-2 px-6 py-4">
										<Button
											size="sm"
											variant="outline"
											onClick={() => openEditEditor(agent)}
											disabled={isAnyOperationRunning}
										>
											<Edit className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
											{t({ id: "editAgent", message: "Edit Agent" })}
										</Button>{" "}
										{isSystemAdminRole(user?.role) && (
											<Button
												size="sm"
												variant="outline"
												className="h-auto min-h-8 whitespace-normal py-2"
												onClick={() => handlePreviewFiles(agent)}
												disabled={
													previewingAgents[agent.id] || isAnyOperationRunning
												}
											>
												{previewingAgents[agent.id] ? (
													<>
														<Loader2 className="w-4 h-4 mr-2 animate-spin rtl:mr-0 rtl:ml-2" />
														{i18n._("agentsPage.generating")}
													</>
												) : (
													<>
														<FileCode2 className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
														{i18n._("agentsPage.previewFiles")}
													</>
												)}
											</Button>
										)}
										<Button
											size="sm"
											variant="outline"
											className="h-auto min-h-8 whitespace-normal py-2"
											onClick={() => handlePromoteToTesting(agent)}
											disabled={
												isStagingDeploymentRunning || isAnyLiveOperationRunning
											}
										>
											{isStagingDeploymentRunning ? (
												<>
													<Loader2 className="w-4 h-4 mr-2 animate-spin rtl:mr-0 rtl:ml-2" />
													{i18n._("agentsPage.deploying")}
												</>
											) : canReadyToTest ? (
												i18n._("agentsPage.deployToTest")
											) : (
												i18n._("agentsPage.redeployToTest")
											)}
											<ArrowUp
												className="h-4 w-4 shrink-0"
												aria-hidden="true"
											/>
										</Button>
										<Button
											size="sm"
											variant="outline"
											className="h-auto min-h-8 whitespace-normal py-2"
											onClick={() => handlePromoteToLive(agent)}
											disabled={
												isAnyLiveOperationRunning || isStagingDeploymentRunning
											}
										>
											{isLiveDeployRunning ? (
												<>
													<Loader2 className="w-4 h-4 mr-2 animate-spin rtl:mr-0 rtl:ml-2" />
													{i18n._("agentsPage.deploying")}
												</>
											) : canGoLive ? (
												i18n._("agentsPage.deployToLive")
											) : (
												i18n._("agentsPage.redeployToLive")
											)}
											<ArrowUp
												className="h-4 w-4 shrink-0"
												aria-hidden="true"
											/>
										</Button>
										<Button
											size="sm"
											variant="outline"
											className="ms-auto h-auto min-h-8 whitespace-normal py-2"
											onClick={() => handleRollbackLive(agent)}
											disabled={
												!canRollback ||
												isAnyLiveOperationRunning ||
												isStagingDeploymentRunning
											}
										>
											{isLiveRollbackRunning ? (
												<>
													<Loader2 className="w-4 h-4 mr-2 animate-spin rtl:mr-0 rtl:ml-2" />
													{i18n._("agentsPage.deploying")}
												</>
											) : (
												<>
													<RotateCcw className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
													{i18n._("agentsPage.rollbackLive")}
												</>
											)}
										</Button>
										<Button
											size="sm"
											variant="destructive"
											className="h-auto min-h-8 whitespace-normal py-2"
											onClick={() => setAgentToDelete(agent)}
											disabled={isAnyOperationRunning}
										>
											<Trash2 className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
											{t({ id: "deleteAgent", message: "Delete Agent" })}
										</Button>
									</div>
								)}
							</CardContent>
						</Card>
					);
				})}
			</div>

			<Dialog
				modal={false}
				open={editorMode !== null}
				onOpenChange={(open) => !open && closeEditor()}
			>
				<DialogContent
					inline
					dir={isRtl ? "rtl" : "ltr"}
					showClose={false}
					className="h-[calc(100vh-8rem)] min-h-[680px] max-w-none overflow-hidden border-0 bg-transparent p-0 text-start shadow-none"
				>
					<DialogHeader className="sr-only">
						<DialogTitle>
							{editorMode === "create"
								? i18n._("agentEditor.title.create")
								: i18n._("agentEditor.title.edit")}
						</DialogTitle>
					</DialogHeader>
					{editorMode && editorAgent && (
						<AgentEditor
							mode={editorMode}
							agent={editorAgent}
							onChange={setEditorAgent}
							onCancel={closeEditor}
							onSave={saveEditor}
							isSaveDisabled={!isEditorDirty}
							isSaving={isSavingEditor}
							hasNewerVersion={hasNewerVersion}
							canEditAdminFields={canManageAgents}
						/>
					)}
				</DialogContent>
			</Dialog>

			<Dialog
				open={previewFilesState !== null}
				onOpenChange={(open) => !open && closePreviewFiles()}
			>
				<DialogContent className="max-w-[96vw] w-full h-[90vh] overflow-hidden sm:max-w-[96vw]">
					<DialogHeader>
						<DialogTitle>
							{i18n._("agentsPage.previewFiles")}
							{previewFilesState ? ` · ${previewFilesState.agentName}` : ""}
						</DialogTitle>
						<DialogDescription>
							{previewFilesState
								? isRtl
									? `נוצרו ${previewFilesState.files.length} קבצים עבור v${previewFilesState.version} בתאריך ${new Date(previewFilesState.generatedAt).toLocaleString()}`
									: `Generated ${previewFilesState.files.length} files for v${previewFilesState.version} at ${new Date(previewFilesState.generatedAt).toLocaleString()}`
								: ""}
						</DialogDescription>
					</DialogHeader>

					<div className="grid min-h-0 flex-1 grid-cols-[280px_minmax(0,1fr)] gap-4 overflow-hidden">
						<div className="min-h-0 overflow-y-auto rounded-md border bg-slate-50 p-2">
							<div className="space-y-1">
								{previewFilesState?.files.map((file) => (
									<button
										key={file.path}
										type="button"
										className={`w-full rounded-md px-3 py-2 text-start text-sm transition-colors ${
											selectedPreviewFile?.path === file.path
												? "bg-card text-slate-900 shadow-sm"
												: "text-slate-600 hover:bg-card/70"
										}`}
										onClick={() => setSelectedPreviewFilePath(file.path)}
									>
										{file.path}
									</button>
								))}
							</div>
						</div>

						<div className="min-h-0 overflow-hidden rounded-md border">
							<div className="flex items-center justify-between border-b px-4 py-3">
								<div className="min-w-0">
									<p className="truncate text-sm font-medium">
										{selectedPreviewFile?.path ||
											i18n._("agentsPage.noFileSelected")}
									</p>
								</div>
								<Button
									size="sm"
									variant="outline"
									onClick={handleCopyPreviewFile}
									disabled={!selectedPreviewFile}
								>
									<Copy className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
									{i18n._("agentsPage.copyToClipboard")}
								</Button>
							</div>
							<div className="h-[calc(90vh-13rem)] overflow-auto bg-slate-950 p-4 text-slate-100">
								<pre className="min-w-full whitespace-pre-wrap break-words font-mono text-sm">
									{selectedPreviewFile?.content || ""}
								</pre>
							</div>
						</div>
					</div>
				</DialogContent>
			</Dialog>

			{agentToDelete && (
				<Dialog
					open={!!agentToDelete}
					onOpenChange={(open) => !open && setAgentToDelete(null)}
				>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>
								{t({ id: "deleteAgent", message: "Delete Agent" })}
							</DialogTitle>
							<DialogDescription>
								{i18n._({
									id: "deleteAgentConfirm",
									message:
										"Are you sure you want to delete {name}? This action cannot be undone.",
									values: { name: agentToDelete.name },
								})}
								<br />
								<span className="text-danger-ink font-bold">
									{i18n._("agentsPage.cannotUndo")}
								</span>
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button variant="outline" onClick={() => setAgentToDelete(null)}>
								{t({ id: "cancel", message: "Cancel" })}
							</Button>
							<Button
								variant="destructive"
								onClick={() => handleDeleteAgent(agentToDelete)}
							>
								{t({ id: "delete", message: "Delete" })}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			)}

			<Dialog
				open={phoneDialogAgent !== null}
				onOpenChange={(open) => !open && setPhoneDialogAgent(null)}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{t({ id: "addPhoneNumber", message: "Add Phone Number" })}
						</DialogTitle>
						<DialogDescription>
							{i18n._("agentsPage.addRealPhone")}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-3">
						<Input
							type="tel"
							value={phoneInput}
							onChange={(e) => setPhoneInput(e.target.value)}
							placeholder="+1 555 123 4567"
							dir="ltr"
						/>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setPhoneDialogAgent(null)}
							>
								{t({ id: "cancel", message: "Cancel" })}
							</Button>
							<Button
								onClick={handleAddPhoneNumber}
								disabled={!phoneInput.trim()}
							>
								{i18n._("agentsPage.add")}
							</Button>
						</DialogFooter>
					</div>
				</DialogContent>
			</Dialog>

			{phoneToRemove && (
				<Dialog
					open={!!phoneToRemove}
					onOpenChange={(open) => !open && setPhoneToRemove(null)}
				>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>
								{t({ id: "removePhoneNumber", message: "Remove Phone Number" })}
							</DialogTitle>
							<DialogDescription>
								<>
									{i18n._("agentsPage.removePhoneConfirmPrefix")}{" "}
									<span className="font-bold" dir="ltr">
										{formatPhoneNumber(phoneToRemove.phone)}
									</span>{" "}
									{i18n._("agentsPage.removePhoneConfirmFrom")}{" "}
									{phoneToRemove.agent.name}?
								</>
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button variant="outline" onClick={() => setPhoneToRemove(null)}>
								{t({ id: "cancel", message: "Cancel" })}
							</Button>
							<Button variant="destructive" onClick={confirmRemovePhoneNumber}>
								{t({ id: "remove", message: "Remove" })}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			)}
		</div>
	);
}
